import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    ActivityIndicator,
    ScrollView,
    StyleSheet,
    Text,
    View,
} from 'react-native';
import { Stack, useLocalSearchParams, usePathname, useRouter } from 'expo-router';
import Header from '../Header';
import Footer from '../Footer';
import MetaTags from '../MetaTags';
import Banner from '../ui/Banner';
import Card from '../ui/Card';
import {
    ProjectActivationCodePrompt,
    ProjectActivationEmailCapture,
} from './ProjectActivationConfirmation';
import ProjectActivationErrorBanner from './ProjectActivationErrorBanner';
import { useResendCooldown } from '../../hooks/useResendCooldown';
import { useCurrentEntitlements } from '../../lib/hooks/useCurrentEntitlements';
import { useProject } from '../../lib/ProjectContext';
import { useSession } from '../../lib/SessionContext';
import colors from '../../lib/colors';
import { buildCanonicalDashboardPath, buildDashboardPath } from '../../lib/dashboardRoutes';
import { getCurrentJourney } from '../../lib/funnelJourney';
import {
    clearOnboardingProjectIntent,
    createOnboardingRequestId,
    finalizeOnboardingProjectIntent,
    loadOrResumeOnboardingProjectIntent,
    requestOnboardingProjectVerification,
    type StoredOnboardingProjectIntent,
} from '../../lib/onboardingProjectIntent';
import {
    OnboardingSubscriptionRequiredError,
    type OnboardingJourneySource,
    type OnboardingProjectIntent,
} from '../../lib/onboardingProjectIntentContract';
import { projectCreationSubscriptionMessage } from '../../lib/projectCreationEntitlements';
import { isProjectActivated } from '../../lib/projectActivation';
import { normalizeOnboardingSeedPaperId } from '../../lib/onboardingProjectIntentRequest';
import { resolveProfileCompletionDestination } from '../../lib/profileCompletion';
import { supabase } from '../../lib/supabase';
import { hasConfirmedEmailIdentity } from '../../utils/authHelpers';
import { isValidEmailAddress, normalizeEmailAddress } from '../../utils/email';
import { getLastEmail, saveLastEmail } from '../../utils/firstTimeUser';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import {
    type LocalizedMessage,
} from '../../lib/i18n/messages';

function withSetupSource(destination: string, isForwarded: boolean): string {
    if (!isForwarded) return destination;
    const [path, query = ''] = destination.split('?');
    const searchParams = new URLSearchParams(query);
    searchParams.set('setup_source', 'forwarded');
    return `${path}?${searchParams.toString()}`;
}

export default function ProjectActivationScreen() {
    const { i18n } = useLingui();
    const router = useRouter();
    const pathname = usePathname();
    const { project, isLoading: projectLoading, error: projectError } = useProject();
    const { session, isLoading: sessionLoading } = useSession();
    const { refreshAuthoritatively: refreshEntitlementsAuthoritatively } = useCurrentEntitlements({
        enabled: false,
    });
    const params = useLocalSearchParams<{
        digestId: string;
        setup_source?: string;
        funnel_source?: string;
        funnel_intent_id?: string;
        activation_generation?: string;
        finalization_request_id?: string;
        seedPaperId?: string;
    }>();
    const projectId = Array.isArray(params.digestId) ? params.digestId[0] : params.digestId;
    const setupSource = Array.isArray(params.setup_source) ? params.setup_source[0] : params.setup_source;
    const sourceParam = Array.isArray(params.funnel_source) ? params.funnel_source[0] : params.funnel_source;
    const intentIdParam = Array.isArray(params.funnel_intent_id)
        ? params.funnel_intent_id[0]
        : params.funnel_intent_id;
    const generationParam = Array.isArray(params.activation_generation)
        ? params.activation_generation[0]
        : params.activation_generation;
    const finalizationRequestIdParam = Array.isArray(params.finalization_request_id)
        ? params.finalization_request_id[0]
        : params.finalization_request_id;
    const seedPaperId = normalizeOnboardingSeedPaperId(params.seedPaperId);
    const callbackFinalizationRequestId = finalizationRequestIdParam
        && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(finalizationRequestIdParam)
        ? finalizationRequestIdParam
        : undefined;
    const isForwarded = setupSource === 'forwarded' || sourceParam === 'forward';
    const journeySource: OnboardingJourneySource = isForwarded
        ? 'forward'
        : sourceParam === 'direct'
            ? 'direct'
            : 'chat';
    const [storedIntent, setStoredIntent] = useState<StoredOnboardingProjectIntent | null>(null);
    const [email, setEmail] = useState('');
    const [verificationRequested, setVerificationRequested] = useState(false);
    const [isPreparingIntent, setIsPreparingIntent] = useState(true);
    const [isRequestingVerification, setIsRequestingVerification] = useState(false);
    const [isFinalizing, setIsFinalizing] = useState(false);
    const [requestError, setRequestError] = useState<LocalizedMessage | null>(null);
    const [finalizationError, setFinalizationError] = useState<LocalizedMessage | null>(null);
    const [finalizationExpired, setFinalizationExpired] = useState(false);
    const [subscriptionRequired, setSubscriptionRequired] = useState(false);
    const [intentRestoreGeneration, setIntentRestoreGeneration] = useState(0);
    const finalizationStartedRef = useRef(false);
    const { resendCooldown, startResendCooldown } = useResendCooldown();
    const callbackGeneration = Number(generationParam);
    const callbackIntent = useMemo<Pick<
        OnboardingProjectIntent,
        'intentId' | 'projectId' | 'activationGeneration'
    > | null>(() => (
        intentIdParam
        && projectId
        && Number.isSafeInteger(callbackGeneration)
        && callbackGeneration > 0
            ? {
                intentId: intentIdParam,
                projectId,
                activationGeneration: callbackGeneration,
            }
            : null
    ), [callbackGeneration, intentIdParam, projectId]);
    const hasConfirmedEmail = hasConfirmedEmailIdentity(session);

    const preparingDestination = useCallback((destination: string) => (
        withSetupSource(destination, isForwarded)
    ), [isForwarded]);

    const navigateToAuthenticatedDestination = useCallback(async (
        destination: string,
        intent?: Pick<OnboardingProjectIntent, 'intentId' | 'projectId' | 'activationGeneration'>,
        finalizationRequestId?: string
    ) => {
        const { data, error } = await supabase.auth.getSession();
        if (error || !hasConfirmedEmailIdentity(data.session)) {
            throw new Error('We could not restore your session. Please try again.');
        }

        const resolved = resolveProfileCompletionDestination({
            session: data.session,
            destination: preparingDestination(destination),
            funnelContext: {
                source: journeySource,
                projectId: intent?.projectId || projectId,
                intentId: intent?.intentId,
                activationGeneration: intent?.activationGeneration,
                finalizationRequestId,
            },
        });
        router.replace(resolved.destination);
    }, [
        journeySource,
        preparingDestination,
        projectId,
        router,
    ]);

    const finalize = useCallback(async (
        intent: Pick<OnboardingProjectIntent, 'intentId' | 'projectId' | 'activationGeneration'>,
        finalizationRequestId: string
    ) => {
        if (finalizationStartedRef.current) return;
        finalizationStartedRef.current = true;
        setIsFinalizing(true);
        setFinalizationError(null);
        setFinalizationExpired(false);
        setSubscriptionRequired(false);
        try {
            const result = await finalizeOnboardingProjectIntent(intent, finalizationRequestId);
            await refreshEntitlementsAuthoritatively();
            await navigateToAuthenticatedDestination(
                result.destination,
                intent,
                finalizationRequestId
            );
        } catch (error) {
            finalizationStartedRef.current = false;
            if (error instanceof OnboardingSubscriptionRequiredError) {
                setSubscriptionRequired(true);
                setFinalizationError(projectCreationSubscriptionMessage);
                return;
            }
            console.error('[ProjectActivation] Could not finalize onboarding project:', error);
            setFinalizationExpired(
                error instanceof Error
                && error.message === 'Project confirmation expired. Create the topic again.'
            );
            setFinalizationError(msg({
                id: 'onboarding.activation.openFailure',
                message: 'We could not open your project. Please try again.',
            }));
        } finally {
            setIsFinalizing(false);
        }
    }, [navigateToAuthenticatedDestination, refreshEntitlementsAuthoritatively]);

    useEffect(() => {
        if (sessionLoading || !projectId) return;
        if (finalizationStartedRef.current) return;
        // A magic-link callback carries the exact intent binding. Let the
        // finalization effect below own navigation even if project realtime
        // reports the adopted project as active first.
        if (callbackIntent) {
            setIsPreparingIntent(false);
            return;
        }
        if (projectLoading || !project) return;
        if (isProjectActivated(project) && hasConfirmedEmail) {
            void navigateToAuthenticatedDestination(
                buildDashboardPath(pathname, `/${projectId}/settings?onboarding=preparing`)
            ).catch((error) => {
                console.error('[ProjectActivation] Could not open activated project:', error);
                setFinalizationError(msg({
                    id: 'onboarding.activation.openFailure',
                    message: 'We could not open your project. Please try again.',
                }));
            });
            return;
        }
        let cancelled = false;
        setIsPreparingIntent(true);
        loadOrResumeOnboardingProjectIntent({
            projectId,
            journeySource,
            chatSessionId: getCurrentJourney()?.chatSessionId ?? null,
            generatedProjectName: project.name || 'Research project',
            seedPaperId,
        }).then(async (stored) => {
            if (cancelled) return;
            const wasRequested = stored.intent.status === 'verification_requested'
                && Boolean(stored.verificationGeneration);
            if (wasRequested) {
                const savedEmail = await getLastEmail();
                if (cancelled) return;
                if (savedEmail) {
                    setEmail(savedEmail);
                    setVerificationRequested(true);
                } else {
                    setVerificationRequested(false);
                }
            } else {
                setVerificationRequested(false);
            }
            setStoredIntent(stored);
        }).catch((error) => {
            if (!cancelled) {
                console.error('[ProjectActivation] Could not restore project setup:', error);
                setRequestError(msg({
                    id: 'onboarding.activation.restoreFailure',
                    message: 'Could not restore project setup.',
                }));
            }
        }).finally(() => {
            if (!cancelled) setIsPreparingIntent(false);
        });
        return () => {
            cancelled = true;
        };
    }, [
        callbackIntent,
        hasConfirmedEmail,
        intentRestoreGeneration,
        journeySource,
        navigateToAuthenticatedDestination,
        pathname,
        project,
        projectId,
        projectLoading,
        sessionLoading,
        seedPaperId,
    ]);

    useEffect(() => {
        if (sessionLoading || finalizationStartedRef.current) return;
        if (callbackIntent) {
            if (hasConfirmedEmail && callbackFinalizationRequestId) {
                void finalize(callbackIntent, callbackFinalizationRequestId);
            } else if (hasConfirmedEmail && !callbackFinalizationRequestId) {
                setFinalizationError(msg({
                    id: 'onboarding.activation.confirmationRestoreFailure',
                    message: 'Project confirmation could not be restored. Request a new confirmation code.',
                }));
            }
            return;
        }
        if (hasConfirmedEmail && storedIntent?.intent.verificationRequired === false) {
            void finalize(storedIntent.intent, storedIntent.directFinalizeClientRequestId);
        }
    }, [
        callbackFinalizationRequestId,
        callbackIntent,
        finalize,
        hasConfirmedEmail,
        sessionLoading,
        storedIntent,
    ]);

    useEffect(() => {
        if (!email && session?.user?.email) {
            setEmail(normalizeEmailAddress(session.user.email));
        }
    }, [email, session?.user?.email]);

    const requestVerification = async () => {
        const normalizedEmail = normalizeEmailAddress(email);
        if (!storedIntent || !isValidEmailAddress(normalizedEmail)) {
            setRequestError(msg({
                id: 'onboarding.activation.invalidEmail',
                message: 'Enter a valid email address.',
            }));
            return;
        }
        setIsRequestingVerification(true);
        setRequestError(null);
        const verificationGeneration = createOnboardingRequestId();
        try {
            const accepted = await requestOnboardingProjectVerification({
                intent: storedIntent.intent,
                email: normalizedEmail,
                verificationGeneration,
                clientRequestId: createOnboardingRequestId(),
            });
            await saveLastEmail(normalizedEmail);
            setEmail(normalizedEmail);
            setStoredIntent({
                ...storedIntent,
                intent: {
                    ...storedIntent.intent,
                    status: 'verification_requested',
                    expiresAt: accepted.expiresAt,
                },
                verificationGeneration,
                finalizationRequestId: accepted.finalizationRequestId,
            });
            setVerificationRequested(true);
            startResendCooldown(60);
        } catch (error) {
            console.error('[ProjectActivation] Could not request verification:', error);
            setRequestError(msg({
                id: 'onboarding.activation.verificationRequestFailure',
                message: 'We could not request a verification code. Please try again.',
            }));
        } finally {
            setIsRequestingVerification(false);
        }
    };

    const handleVerified = async () => {
        if (!storedIntent) {
            setFinalizationError(msg({
                id: 'onboarding.activation.confirmationMissing',
                message: 'Project confirmation could not be restored. Reload and try again.',
            }));
            return;
        }
        if (!storedIntent.finalizationRequestId) {
            setFinalizationError(msg({
                id: 'onboarding.activation.confirmationMissing',
                message: 'Project confirmation could not be restored. Reload and try again.',
            }));
            return;
        }
        await finalize(storedIntent.intent, storedIntent.finalizationRequestId);
    };

    const handleUseDifferentEmail = () => {
        setVerificationRequested(false);
        setRequestError(null);
        setFinalizationError(null);
    };

    const handleRestoreConfirmation = () => {
        router.replace(withSetupSource(
            buildDashboardPath(pathname, `/${projectId}/activate`),
            isForwarded
        ));
    };

    const handleRestartExpiredConfirmation = async () => {
        if (!projectId) return;

        setIsPreparingIntent(true);
        setStoredIntent(null);
        setVerificationRequested(false);
        setRequestError(null);
        setFinalizationError(null);
        setFinalizationExpired(false);
        try {
            await clearOnboardingProjectIntent(projectId);
            if (callbackIntent) {
                handleRestoreConfirmation();
                return;
            }
            setIntentRestoreGeneration((generation) => generation + 1);
        } catch (error) {
            console.error('[ProjectActivation] Could not restart project confirmation:', error);
            setIsPreparingIntent(false);
            setRequestError(msg({
                id: 'onboarding.activation.restoreFailure',
                message: 'Could not restore project setup.',
            }));
        }
    };

    const pageTitle = project?.name || i18n._(msg({
        id: 'onboarding.activation.defaultProjectTitle',
        message: 'Your research project',
    }));
    const retryIntent = callbackIntent || storedIntent?.intent;
    const retryFinalizationRequestId = callbackIntent
        ? callbackFinalizationRequestId
        : storedIntent?.finalizationRequestId
            || (
                storedIntent?.intent.verificationRequired === false
                    ? storedIntent.directFinalizeClientRequestId
                    : undefined
            );

    return (
        <>
            <MetaTags
                title={`Confirm ${pageTitle} - Paperzilla`}
                description="Confirm your email to open your first Paperzilla matches."
                url={`https://paperzilla.ai${buildCanonicalDashboardPath(`/${projectId}/activate`)}`}
            />
            <Stack.Screen options={{ headerShown: false }} />
            <View style={styles.page}>
                <Header hidePreviewTrialBanner />
                <ScrollView contentContainerStyle={styles.scrollContent}>
                    <View style={styles.content}>
                        <Text style={styles.title} accessibilityRole="header">{pageTitle}</Text>

                        {(projectLoading || isPreparingIntent || isFinalizing) && (
                            <Card>
                                <View style={styles.loadingRow}>
                                    <ActivityIndicator color={colors.primaryGreen} />
                                    <Text style={styles.loadingText}>
                                        {isFinalizing ? <Trans id="onboarding.projectactivationscreen.openingYourMatches" comment="Conditional UI copy in components/onboarding/ProjectActivationScreen.tsx">Opening your matches…</Trans> : <Trans id="onboarding.projectactivationscreen.restoringYourProject" comment="Conditional UI copy in components/onboarding/ProjectActivationScreen.tsx">Restoring your project…</Trans>}
                                    </Text>
                                </View>
                            </Card>
                        )}
                        {projectError && <Banner
                            type="error"
                            title={msg({ id: 'onboarding.activation.projectUnavailable', message: 'Project unavailable' })}
                            message={msg({ id: 'onboarding.activation.projectUnavailableMessage', message: 'We could not load this project. Please try again.' })}
                        />}
                        {requestError && !verificationRequested && <Banner
                            type="error"
                            title={msg({ id: 'onboarding.activation.codeError', message: 'Could not send code' })}
                            message={requestError}
                        />}
                        <ProjectActivationErrorBanner
                            error={finalizationError}
                            subscriptionRequired={subscriptionRequired}
                            expired={finalizationExpired}
                            onStartTrial={() => router.push('/trial')}
                            onRestart={() => void handleRestartExpiredConfirmation()}
                            onRetry={retryIntent && retryFinalizationRequestId
                                ? () => void finalize(retryIntent, retryFinalizationRequestId)
                                : undefined}
                            onRestore={callbackIntent ? handleRestoreConfirmation : undefined}
                        />

                        {!isFinalizing
                            && !projectLoading
                            && !isPreparingIntent
                            && !verificationRequested
                            && storedIntent?.intent.verificationRequired ? (
                            <ProjectActivationEmailCapture
                                email={email}
                                isRequesting={isRequestingVerification}
                                onChangeEmail={setEmail}
                                onRequestVerification={() => void requestVerification()}
                            />
                        ) : null}

                        {verificationRequested
                            && !projectLoading
                            && !isPreparingIntent
                            && !isFinalizing ? (
                            <ProjectActivationCodePrompt
                                email={email}
                                isRequesting={isRequestingVerification}
                                requestError={requestError}
                                resendCooldown={resendCooldown}
                                onVerified={handleVerified}
                                onResend={() => void requestVerification()}
                                onUseDifferentEmail={handleUseDifferentEmail}
                            />
                        ) : null}
                    </View>
                    <Footer />
                </ScrollView>
            </View>
        </>
    );
}

const styles = StyleSheet.create({
    page: { flex: 1, backgroundColor: colors.bgWeak },
    scrollContent: { flexGrow: 1 },
    content: {
        width: '100%',
        maxWidth: 760,
        alignSelf: 'center',
        paddingHorizontal: 20,
        paddingTop: 40,
        paddingBottom: 60,
        gap: 16,
    },
    title: { color: colors.text900, fontSize: 30, fontWeight: '800', lineHeight: 38 },
    loadingRow: { alignItems: 'center', flexDirection: 'row', gap: 10 },
    loadingText: { color: colors.text600, fontSize: 15 },
});
